import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from './Header';
import { Sidebar, SidebarItem } from './Sidebar';
import { getDefaultSidebarItems } from './sidebarConstants';
import { Footer } from './Footer';
import { NotificationsModal } from './NotificationsModal';
import { defaultNotifications } from './notificationConstants';
import { useSchedulingAccess } from '../../pages/Calendars/scheduling/hooks';

export interface MainLayoutProps {
  children: React.ReactNode;
  user?: {
    name: string;
    email: string;
    role: string;
    avatar?: string;
  };
  sidebarItems?: SidebarItem[];
  activeItem?: string;
  onSidebarItemClick?: (item: SidebarItem) => void;
  onProfileAction?: (action: string) => void;
  isAuthenticated?: boolean;
  className?: string;
}

export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  user = {
    name: 'John Doe',
    email: 'john.doe@acmecorp.com',
    role: 'Organization Admin',
  },
  sidebarItems,
  activeItem,
  onSidebarItemClick,
  onProfileAction,
  isAuthenticated = true,
  className,
}) => {
  const { t } = useTranslation();
  const [orgRole, setOrgRole] = useState<string>(
    typeof window !== 'undefined' ? localStorage.getItem('organization_role') || '' : ''
  );

  useEffect(() => {
    const handleRoleChange = () => {
      setOrgRole(localStorage.getItem('organization_role') || '');
    };
    window.addEventListener('organizationRoleChanged', handleRoleChange);
    return () => window.removeEventListener('organizationRoleChanged', handleRoleChange);
  }, []);

  const { data: schedulingAccess } = useSchedulingAccess();
  const defaultSidebarItems =
    sidebarItems ||
    getDefaultSidebarItems(t, {
      role: orgRole,
      schedulingEnabled: schedulingAccess?.enabled ?? false,
    });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleMobileSidebarToggle = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const handleNotificationClick = () => {
    setIsNotificationsOpen(true);
  };

  const handleCloseNotifications = () => {
    setIsNotificationsOpen(false);
  };

  const handleSidebarItemClick = (item: SidebarItem) => {
    onSidebarItemClick?.(item);
    setIsMobileSidebarOpen(false);
  };

  const handleProfileAction = (action: string) => {
    onProfileAction?.(action);
  };

  return (
    <div
      className={`flex flex-col min-h-screen bg-white dark:bg-gray-900 brand-main-bg ${className || ''}`}
    >
      <Header
        onToggleSidebar={handleToggleSidebar}
        onMobileSidebarToggle={handleMobileSidebarToggle}
        onNotificationClick={handleNotificationClick}
        onProfileAction={handleProfileAction}
        user={user}
        notificationCount={defaultNotifications.filter(n => !n.isRead).length}
        isAuthenticated={isAuthenticated}
      />

      <div className="flex flex-1 relative">
        {isMobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                setIsMobileSidebarOpen(false);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Close sidebar"
          />
        )}

        <Sidebar
          items={defaultSidebarItems}
          activeItem={activeItem}
          onItemClick={handleSidebarItemClick}
          isCollapsed={isSidebarCollapsed}
          isMobileOpen={isMobileSidebarOpen}
        />

        <main className="flex-1 min-w-0 bg-white dark:bg-gray-900 brand-main-bg overflow-x-hidden relative">
          <div className="h-full w-full">{children}</div>
        </main>
      </div>

      <Footer />
      <NotificationsModal
        isOpen={isNotificationsOpen}
        onClose={handleCloseNotifications}
        notifications={defaultNotifications}
        onMarkAsRead={_id => {}}
        onMarkAllAsRead={() => {}}
        onViewAll={() => {}}
      />
    </div>
  );
};
